import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatQuantity, type UnitKey } from '@fewerbags/core';
import { STORAGE_LOCATIONS } from '@fewerbags/schema';
import type { IngredientSummary, PantryItemDto } from '@fewerbags/schema';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Field } from '../Field';
import { SelectField } from '../SelectField';
import { IngredientAutocomplete } from '../ingredients/IngredientAutocomplete';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import {
  dateInputToIsoDatetime,
  expiresOnSourceLabel,
  LOCATION_LABEL,
  type StorageLocation,
} from '../../lib/pantryDisplay';
import { UNIT_OPTIONS } from '../../lib/units';

const LOCATION_OPTIONS = STORAGE_LOCATIONS.map((l) => ({ value: l, label: LOCATION_LABEL[l] }));

/** Add-item form: ingredient autocomplete (or free text), quantity/unit, location, optional dates. */
export function AddPantryItemForm({ onAdded }: { onAdded: () => void }) {
  const [ingredientText, setIngredientText] = useState('');
  const [selected, setSelected] = useState<IngredientSummary | null>(null);
  const [qty, setQty] = useState('1');
  const [unit, setUnit] = useState<UnitKey>('count');
  const [location, setLocation] = useState<StorageLocation>('pantry');
  const [expiresOn, setExpiresOn] = useState('');
  const [purchasedAt, setPurchasedAt] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<PantryItemDto | null>(null);
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: (input: Parameters<typeof api.pantry.create>[0]) => api.pantry.create(input),
    onSuccess: (item) => {
      setCreated(item);
      setIngredientText('');
      setSelected(null);
      setQty('1');
      setExpiresOn('');
      setPurchasedAt('');
      void qc.invalidateQueries({ queryKey: ['pantry'] });
      onAdded();
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    setCreated(null);
    const name = ingredientText.trim();
    if (!selected && !name) {
      setFormError('Choose an ingredient, or type its name.');
      return;
    }
    const qtyNum = Number(qty);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      setFormError('Enter a quantity greater than 0.');
      return;
    }
    const purchasedIso = dateInputToIsoDatetime(purchasedAt);
    create.mutate({
      ...(selected ? { ingredientId: selected.id } : { name }),
      qty: qtyNum,
      unit,
      location,
      ...(expiresOn ? { expiresOn } : {}),
      ...(purchasedIso ? { purchasedAt: purchasedIso } : {}),
    });
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
      <IngredientAutocomplete
        id="pantry-add-ingredient"
        label="Ingredient"
        value={ingredientText}
        onChange={(text) => {
          setIngredientText(text);
          setSelected(null);
        }}
        onSelect={(ing) => {
          setSelected(ing);
          setIngredientText(ing.canonicalName);
          setUnit(ing.defaultUnit);
        }}
        placeholder="Start typing…"
        helpText="Pick a suggestion, or type a new ingredient name."
      />
      <div className="grid grid-cols-2 gap-3">
        <Field
          id="pantry-add-qty"
          name="qty"
          label="Quantity"
          type="number"
          inputMode="decimal"
          step="any"
          min="0"
          required
          value={qty}
          onChange={(e) => setQty(e.target.value)}
        />
        <SelectField
          id="pantry-add-unit"
          label="Unit"
          options={UNIT_OPTIONS}
          value={unit}
          onChange={(e) => setUnit(e.target.value as UnitKey)}
        />
      </div>
      <SelectField
        id="pantry-add-location"
        label="Location"
        options={LOCATION_OPTIONS}
        value={location}
        onChange={(e) => setLocation(e.target.value as StorageLocation)}
      />
      <div className="grid grid-cols-2 gap-3">
        <Field
          id="pantry-add-expires"
          name="expiresOn"
          label="Expiry date (optional)"
          type="date"
          value={expiresOn}
          onChange={(e) => setExpiresOn(e.target.value)}
        />
        <Field
          id="pantry-add-purchased"
          name="purchasedAt"
          label="Purchased date (optional)"
          type="date"
          value={purchasedAt}
          onChange={(e) => setPurchasedAt(e.target.value)}
        />
      </div>
      {formError ? <Alert tone="error">{formError}</Alert> : null}
      {create.isError ? <Alert tone="error">{messageForError(create.error)}</Alert> : null}
      {created ? (
        <Alert tone="success">
          Added {formatQuantity(created.qty, created.unit)} {created.ingredientName} to{' '}
          {LOCATION_LABEL[created.location]}. {expiresOnSourceLabel(created.expiresOnSource)}.
        </Alert>
      ) : null}
      <Button type="submit" disabled={create.isPending}>
        {create.isPending ? 'Adding…' : 'Add item'}
      </Button>
    </form>
  );
}
